import { DASHBOARD_COLUMNS } from '@clockwise/semantic-core';
import type { Layout } from 'react-grid-layout';

// `Layout` is the whole arrangement (a readonly `LayoutItem[]`) in
// react-grid-layout v2 — not a single item as in v1.

/**
 * Grid key reserved for the drilldown row. It is never part of a dashboard's
 * own layout — {@link layoutWithDrill} injects it and {@link stripDrill}
 * removes it again before the layout is persisted, so a drill that is open
 * when the user drags a tile can't leak into the saved arrangement.
 */
export const DRILL_KEY = '__drill';

/**
 * Grid rows the drilldown panel occupies.
 *
 * Sized to the cohorts view, which is the one it opens on. The table and
 * distribution views scroll inside the panel when they need more room — the
 * "tiles never scroll" rule is about tiles, and a panel that resized itself
 * every time the reader switched view would shunt the rest of the dashboard up
 * and down under them.
 */
export const DRILL_H = 13;

/**
 * Insert the drilldown as a full-width grid row **directly beneath the tile it
 * was opened from**, pushing everything below it down.
 *
 * Rendering the drill inline (rather than in a modal or at the page bottom)
 * keeps the source tile and its detail visible together — the user never loses
 * the context they clicked from. `sourceKey` is the grid key of that tile.
 *
 * The insertion point is the bottom of the source tile's *row*, not the tile
 * itself: side-by-side tiles must not be split by the panel.
 */
export function layoutWithDrill(base: Layout, sourceKey: string | null): Layout {
  if (!sourceKey) return base;
  const src = base.find((l) => l.i === sourceKey);
  if (!src) return base;

  // Every tile that vertically overlaps the source shares its row; the drill
  // goes below the lowest edge among them.
  const rowBottom = Math.max(
    ...base.filter((l) => l.y < src.y + src.h && l.y + l.h > src.y).map((l) => l.y + l.h),
  );

  return [
    ...base.map((l) => (l.y >= rowBottom ? { ...l, y: l.y + DRILL_H } : l)),
    {
      i: DRILL_KEY,
      x: 0,
      y: rowBottom,
      w: DASHBOARD_COLUMNS,
      h: DRILL_H,
      // `static`, not merely un-draggable. `isDraggable: false` alone stops the
      // user picking the row up but still lets the compactor move it when
      // another tile is dropped nearby — and {@link stripDrill} reads the
      // insertion point back off this row's `y`, so a displaced row makes the
      // un-shift subtract from the wrong tiles.
      static: true,
      isDraggable: false,
      isResizable: false,
    },
  ];
}

/**
 * The inverse of {@link layoutWithDrill}: drop the drill row **and undo the
 * downward shift it caused**. Call before storing a layout.
 *
 * Removing the row alone is not enough. react-grid-layout echoes the current
 * arrangement back on every interaction, so a drag while a drill is open would
 * otherwise persist the shifted positions — and closing the drill would leave
 * every tile below it stranded {@link DRILL_H} rows down, permanently.
 *
 * Tiles the user dragged *into* the drill's own band can't be un-shifted
 * unambiguously; they keep the position they were dropped at, which is the
 * behaviour the user just asked for anyway.
 */
export function stripDrill(layout: Layout): Layout {
  const drill = layout.find((l) => l.i === DRILL_KEY);
  const rest = layout.filter((l) => l.i !== DRILL_KEY);
  if (!drill) return rest;
  const shiftedFrom = drill.y + DRILL_H;
  return rest.map((l) => (l.y >= shiftedFrom ? { ...l, y: l.y - DRILL_H } : l));
}
